import { Link, useParams, Navigate } from "react-router-dom";
import { SERVICE_MAP, SERVICES, getServiceMeta } from "@/data/services";
import { ArrowRight, CheckSquare, FileText } from "@phosphor-icons/react";
import FigureOnMap from "@/components/FigureOnMap";

export default function ServiceDetail() {
  const { slug } = useParams();
  const svc = SERVICE_MAP[slug];
  if (!svc) return <Navigate to="/services" replace />;

  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];
  const meta = getServiceMeta(slug);
  const figId = `FIG.${String(idx + 1).padStart(2, "0")}`;

  return (
    <div data-testid={`service-detail-${slug}`}>
      {/* Hero */}
      <section className="relative bg-zinc-50 border-b border-zinc-200 overflow-hidden ruler-top">
        <div className="absolute inset-0 bg-blueprint-grid pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-14 md:pb-20">
          <nav className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-6">
            <Link to="/services" className="hover:text-zinc-900">Services</Link>
            <span className="mx-2">/</span>
            <span className="text-zinc-900">{svc.title}</span>
          </nav>
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
                Service {String(idx + 1).padStart(2, "0")} / 13
              </div>
              <h1
                className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tighter font-medium text-zinc-950 leading-[1.0] text-balance"
                data-testid="service-detail-title"
              >
                {svc.title}
              </h1>
              <p className="mt-6 text-lg text-zinc-600 max-w-2xl leading-relaxed">
                {svc.short}
              </p>
            </div>
            <div className="md:col-span-4 md:justify-self-end">
              <div className="flex flex-col gap-2">
                <Link
                  to={`/free-trial?service=${svc.slug}`}
                  className="px-5 py-3 bg-[#0044FF] text-white font-medium btn-lift inline-flex items-center justify-between gap-2"
                  data-testid="service-cta-free-trial"
                >
                  Start a free trial <ArrowRight size={14} weight="bold" />
                </Link>
                <Link
                  to={`/contact?service=${svc.slug}`}
                  className="px-5 py-3 border border-zinc-900 text-zinc-950 font-medium btn-lift hover:bg-zinc-900 hover:text-white inline-flex items-center justify-between gap-2"
                  data-testid="service-cta-contact"
                >
                  Request a quote <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white py-20 md:py-28 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
              What this service covers
            </div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-zinc-950 leading-tight">
              Drafted for your stamp. Reviewed for your jurisdiction.
            </h2>
            <p className="mt-6 text-zinc-600 leading-relaxed text-lg">{svc.description}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-zinc-900 p-8 bg-zinc-50">
              <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 pb-4 mb-5 border-b border-dashed border-zinc-300 flex items-center justify-between">
                <span>Deliverables</span>
                <span>FIG.D</span>
              </div>
              <ul className="space-y-3">
                {svc.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-3">
                    <CheckSquare size={18} weight="duotone" className="text-[#0044FF] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-zinc-800">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Survey-type heading + process + figure on map */}
      <section
        className="bg-white border-b border-zinc-200 py-20 md:py-28"
        data-testid="service-process-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-7">
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
                Survey type · Our process
              </div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight text-zinc-950 leading-[1.05]">
                {meta.heading}
              </h2>
              <p className="mt-5 text-zinc-600 text-lg leading-relaxed max-w-2xl">
                {meta.intro}
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <div className="grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
                {meta.process.map((p, i) => (
                  <div
                    key={p.label}
                    className="bg-white p-6 flex flex-col"
                    data-testid={`process-step-${i + 1}`}
                  >
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                      Step
                    </div>
                    <div className="font-display text-5xl text-zinc-950 tracking-tighter leading-none mt-2">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="mt-5 text-base font-medium text-zinc-950">
                      {p.label}
                    </h3>
                    <p className="mt-2 text-sm text-zinc-600 leading-relaxed flex-1">
                      {p.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <FigureOnMap figure={meta.figure} figId={figId} testId={`figure-${slug}`} />
            </div>
          </div>
        </div>
      </section>

      {/* What we need */}
      <section className="bg-zinc-50 py-20 md:py-28 border-b border-zinc-200" data-testid="documents-required">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 mb-10">
            <div className="md:col-span-6">
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
                What we need to get started
              </div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight text-zinc-950 leading-[1.05]">
                Your checklist for a {svc.title.toLowerCase()}.
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 self-end">
              <p className="text-zinc-600 leading-relaxed">
                The more complete this package, the faster (and cleaner) your drawing.
                Don't have everything? Send what you have — we'll flag gaps in our intake review.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
            {svc.documents.map((d, i) => (
              <div
                key={d}
                className="bg-white p-5 md:p-6 flex items-start gap-4"
                data-testid={`doc-item-${i}`}
              >
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 pt-0.5 w-8">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <FileText size={20} weight="duotone" className="text-[#0044FF] mt-0.5 flex-shrink-0" />
                <div className="text-sm text-zinc-800 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-zinc-200 pt-8">
            <p className="text-sm text-zinc-600">
              Missing something? Upload what you have — we review every brief.
            </p>
            <Link
              to={`/free-trial?service=${svc.slug}`}
              className="px-5 py-3 bg-zinc-950 text-white font-medium btn-lift inline-flex items-center gap-2"
              data-testid="docs-free-trial"
            >
              Upload your sample data <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* Navigate to next service */}
      <section className="bg-white py-14 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between gap-4">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500">Next service</div>
            <div className="font-display text-2xl md:text-3xl text-zinc-950 tracking-tight mt-1">
              {next.title}
            </div>
          </div>
          <Link
            to={`/services/${next.slug}`}
            className="px-5 py-3 border border-zinc-900 text-zinc-950 font-medium btn-lift hover:bg-zinc-900 hover:text-white inline-flex items-center gap-2"
            data-testid="next-service-link"
          >
            View <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
